// SyllablePlayer.js - Component for syllable-by-syllable pronunciation
import React, { useState, useEffect } from 'react';
import { getWordData, soundData } from '../data/phoneticData';

const SyllablePlayer = ({ word, onBack }) => {
  const [currentSyllableIndex, setCurrentSyllableIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playMode, setPlayMode] = useState('syllable'); // 'syllable', 'vowel', 'consonant', 'whole'
  const [currentSoundIndex, setCurrentSoundIndex] = useState(0);
  const [wordData, setWordData] = useState(null);

  useEffect(() => {
    const data = getWordData(word);
    setWordData(data);
    setCurrentSyllableIndex(0);
    setCurrentSoundIndex(0);
  }, [word]);

  const playSound = (text, rate = 0.5) => {
    if (!('speechSynthesis' in window)) return;
    
    setIsPlaying(true);
    const voices = window.speechSynthesis.getVoices();
    const selectedVoice = voices.find(voice => 
      voice.lang.startsWith('en') && 
      (voice.name.includes('Google') || voice.name.includes('Microsoft') || voice.name.includes('Neural'))
    ) || voices.find(voice => voice.lang.startsWith('en-US')) || voices[0];
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.voice = selectedVoice;
    utterance.rate = rate;
    utterance.pitch = 1.0;
    utterance.volume = 0.9;
    utterance.lang = 'en-US';
    
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);
    
    window.speechSynthesis.speak(utterance);
  };

  const playCurrentSyllable = () => {
    if (!wordData) return;
    const syllable = wordData.syllables[currentSyllableIndex];
    playSound(syllable.text, 0.4);
  };

  const playCurrentVowel = () => {
    if (!wordData) return;
    const syllable = wordData.syllables[currentSyllableIndex];
    if (syllable.vowels[currentSoundIndex]) {
      const vowelSound = soundData.vowels[syllable.vowels[currentSoundIndex]];
      if (vowelSound) {
        playSound(vowelSound.example, 0.3);
      }
    }
  };

  const playCurrentConsonant = () => {
    if (!wordData) return;
    const syllable = wordData.syllables[currentSyllableIndex];
    if (syllable.consonants[currentSoundIndex]) {
      const consonantSound = soundData.consonants[syllable.consonants[currentSoundIndex]];
      if (consonantSound) {
        playSound(consonantSound.example, 0.3);
      }
    }
  };

  const playWholeWord = () => {
    if (!wordData) return;
    playSound(wordData.word, 0.6);
  };

  const nextSyllable = () => {
    if (wordData && currentSyllableIndex < wordData.syllables.length - 1) {
      setCurrentSyllableIndex(currentSyllableIndex + 1);
      setCurrentSoundIndex(0);
    }
  };

  const prevSyllable = () => {
    if (currentSyllableIndex > 0) {
      setCurrentSyllableIndex(currentSyllableIndex - 1);
      setCurrentSoundIndex(0);
    }
  };

  const nextSound = () => {
    if (!wordData) return;
    const syllable = wordData.syllables[currentSyllableIndex];
    const totalSounds = syllable.vowels.length + syllable.consonants.length;
    if (currentSoundIndex < totalSounds - 1) {
      setCurrentSoundIndex(currentSoundIndex + 1);
    }
  };

  const prevSound = () => {
    if (currentSoundIndex > 0) {
      setCurrentSoundIndex(currentSoundIndex - 1);
    }
  };

  if (!wordData) {
    return <div>Loading...</div>;
  }

  const currentSyllable = wordData.syllables[currentSyllableIndex];
  const currentVowel = currentSyllable.vowels[currentSoundIndex];
  const currentConsonant = currentSyllable.consonants[currentSoundIndex];

  return (
    <div className="syllable-player">
      <header>
        <button onClick={onBack}>← Back</button>
        <h1>Syllable Breakdown: {wordData.word}</h1>
      </header>

      <div className="word-overview">
        <h2>{wordData.word}</h2>
        <div className="phonetic">{wordData.phonetic}</div>
        <div className="definition">{wordData.definition}</div>
      </div>

      <div className="play-mode-selector">
        <h3>Play Mode:</h3>
        <div className="mode-buttons">
          <button 
            className={playMode === 'whole' ? 'active' : ''}
            onClick={() => setPlayMode('whole')}
          >
            🔊 Whole Word
          </button>
          <button 
            className={playMode === 'syllable' ? 'active' : ''}
            onClick={() => setPlayMode('syllable')}
          >
            📝 Syllable
          </button>
          <button 
            className={playMode === 'vowel' ? 'active' : ''}
            onClick={() => setPlayMode('vowel')}
          >
            🗣️ Vowel
          </button>
          <button 
            className={playMode === 'consonant' ? 'active' : ''}
            onClick={() => setPlayMode('consonant')}
          >
            👄 Consonant
          </button>
        </div>
      </div>

      {playMode === 'whole' && (
        <div className="whole-word-section">
          <button 
            className="play-button large"
            onClick={playWholeWord}
            disabled={isPlaying}
          >
            🔊 {isPlaying ? 'Playing...' : 'Play Whole Word'}
          </button>
        </div>
      )}

      {playMode === 'syllable' && (
        <div className="syllable-section">
          <div className="syllable-navigation">
            <button onClick={prevSyllable} disabled={currentSyllableIndex === 0}>
              ← Previous
            </button>
            <span className="syllable-counter">
              {currentSyllableIndex + 1} of {wordData.syllables.length}
            </span>
            <button 
              onClick={nextSyllable} 
              disabled={currentSyllableIndex === wordData.syllables.length - 1}
            >
              Next →
            </button>
          </div>

          <div className="current-syllable">
            <h3>Syllable: {currentSyllable.text}</h3>
            <div className="phonetic">{currentSyllable.phonetic}</div>
            <button 
              className="play-button"
              onClick={playCurrentSyllable}
              disabled={isPlaying}
            >
              🔊 {isPlaying ? 'Playing...' : 'Play Syllable'}
            </button>
          </div>

          <div className="syllable-breakdown">
            <div className="sounds-section">
              <h4>Vowels:</h4>
              <div className="sounds">
                {currentSyllable.vowels.map((vowel, index) => {
                  const vowelData = soundData.vowels[vowel];
                  return (
                    <div key={index} className="sound-item">
                      <span className="sound-symbol">/{vowel}/</span>
                      <span className="sound-description">{vowelData?.description}</span>
                      <button 
                        className="play-sound-btn"
                        onClick={() => playSound(vowelData?.example || vowel, 0.3)}
                      >
                        🔊
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="sounds-section">
              <h4>Consonants:</h4>
              <div className="sounds">
                {currentSyllable.consonants.map((consonant, index) => {
                  const consonantData = soundData.consonants[consonant];
                  return (
                    <div key={index} className="sound-item">
                      <span className="sound-symbol">/{consonant}/</span>
                      <span className="sound-description">{consonantData?.description}</span>
                      <button 
                        className="play-sound-btn"
                        onClick={() => playSound(consonantData?.example || consonant, 0.3)}
                      >
                        🔊
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {playMode === 'vowel' && (
        <div className="vowel-section">
          <div className="sound-navigation">
            <button onClick={prevSound} disabled={currentSoundIndex === 0}>
              ← Previous
            </button>
            <span className="sound-counter">
              Sound {currentSoundIndex + 1}
            </span>
            <button onClick={nextSound}>
              Next →
            </button>
          </div>

          {currentVowel && (
            <div className="current-sound">
              <h3>Vowel: /{currentVowel}/</h3>
              <div className="sound-details">
                <p><strong>Description:</strong> {soundData.vowels[currentVowel]?.description}</p>
                <p><strong>Example:</strong> {soundData.vowels[currentVowel]?.example}</p>
                <p><strong>Mouth Position:</strong> {soundData.vowels[currentVowel]?.mouthPosition}</p>
              </div>
              <button 
                className="play-button"
                onClick={playCurrentVowel}
                disabled={isPlaying}
              >
                🔊 {isPlaying ? 'Playing...' : 'Play Vowel'}
              </button>
            </div>
          )}
        </div>
      )}

      {playMode === 'consonant' && (
        <div className="consonant-section">
          <div className="sound-navigation">
            <button onClick={prevSound} disabled={currentSoundIndex === 0}>
              ← Previous
            </button>
            <span className="sound-counter">
              Sound {currentSoundIndex + 1}
            </span>
            <button onClick={nextSound}>
              Next →
            </button>
          </div>

          {currentConsonant && (
            <div className="current-sound">
              <h3>Consonant: /{currentConsonant}/</h3>
              <div className="sound-details">
                <p><strong>Description:</strong> {soundData.consonants[currentConsonant]?.description}</p>
                <p><strong>Example:</strong> {soundData.consonants[currentConsonant]?.example}</p>
                <p><strong>Mouth Position:</strong> {soundData.consonants[currentConsonant]?.mouthPosition}</p>
              </div>
              <button 
                className="play-button"
                onClick={playCurrentConsonant}
                disabled={isPlaying}
              >
                🔊 {isPlaying ? 'Playing...' : 'Play Consonant'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="all-syllables">
        <h3>All Syllables:</h3>
        <div className="syllables-grid">
          {wordData.syllables.map((syllable, index) => (
            <div 
              key={index} 
              className={`syllable-item ${index === currentSyllableIndex ? 'active' : ''}`}
              onClick={() => setCurrentSyllableIndex(index)}
            >
              <div className="syllable-text">{syllable.text}</div>
              <div className="syllable-phonetic">{syllable.phonetic}</div>
              <button 
                className="play-syllable-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  playSound(syllable.text, 0.4);
                }}
              >
                🔊
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SyllablePlayer;
